/*
 * cf-gate-only-section.js — Click Fencing
 *
 * ORIGINAL PURPOSE (2026-08-30)
 * A gate-only section — the gate between its own two posts, no panels — computed correctly
 * and priced correctly, but two things then dropped it:
 *   1. validateSection() refused it, so it could not reach Review & Checkout.
 *   2. updateFinalSectionsPreview() skipped any section with numPanels === 0, so it did not
 *      draw there at all.
 * This patch worked around both: it wrapped validateSection to let a gate-only section
 * through, and injected a crude div "strip" (two black bars and a blue box) in place of the
 * missing drawing.
 *
 * 2026-09-07 UPDATE — flat top now fixes both AT SOURCE
 * Pete: "section 2 isnt drawing ? fix this".
 * assets/flat-top.js now (a) reports the real length on an element-only layout instead of
 * total: 0, so validateSection passes it on its own, and (b) lets element-only sections
 * through updateFinalSectionsPreview so they draw with the NORMAL renderSectionPreview
 * engine — a real SVG fence, identical to the Configure step, not a placeholder.
 *
 * Measured on the preview theme before this edit: a 1100mm gate-only flat-top section
 * rendered TWICE on Review — the real SVG ("50mm Post | 970mm Gate | 50mm Post") followed
 * by this patch's placeholder ("50 970 Gate 50").
 *
 * So the placeholder now STANDS DOWN whenever the section has already been drawn properly.
 * It is deliberately NOT deleted: blade.js and perfmesh-calc.js have NOT had the source fix,
 * and they still need both the validateSection rescue and the placeholder. When those two
 * are fixed at source as well, this whole file can be dropped from meta-tags.liquid.
 *
 * The validateSection wrapper is left exactly as it was — on flat top it is now redundant
 * (the source returns a real total), but it is harmless there and still load-bearing on the
 * other two calculators.
 */
(function () {
  "use strict";

  if (!/(blade-fence-calculator|flat-top-fence-calculator|perfmesh-fence)/.test(location.pathname)) return;

  var TRIES = 0, MAX_TRIES = 200;

  function inst() { return window.glassCalculator || window.glassFencingCalculator || null; }

  // 2026-09-07: this used to accept ANY gate section with no panels, which meant a section
  // far too short to build was waved through. Measured on flat top before this edit:
  //   400mm gate-only  -> validateSection true, no layout at all, quote $777.70, zero posts
  //   800mm gate+rake  -> validateSection true, quote $580.21 (1200 rake + 970 gate in an
  //                       800mm run), and the drawing still showed the PREVIOUS fence
  // The source comment in _calculateSpreadCore warned about exactly this ("keying off
  // hasGate let a 400mm section draw a 1070mm fence and quote it").
  //
  // The solvers now report a real total for an in-band element-only run and 0/undefined for
  // a section that is simply too short (flat-top.js, blade.js and perfmesh-calc.js all
  // carry that fix), so total is the honest in-band marker. Verified on blade 2026-09-07:
  //   600mm, 900mm  -> total undefined, perfomesh false  (correctly refused)
  //   1050mm, 1100mm-> total 1050 / 1100, perfomesh true (real gate-only runs)
  function isGateOnly(fc, n) {
    try {
      var s = fc.state && fc.state.sections && fc.state.sections[n];
      var c = fc.state && fc.state.optimizedConfigs && fc.state.optimizedConfigs[n];
      if (!s || !s.hasGate || !c || !c.valid || (c.gatePanels || 0) <= 0) return false;
      // Must be a layout the solver actually found, not merely "no panels".
      if (!((parseInt(c.total, 10) || 0) > 0) && !c.perfomesh) return false;
      var noPanels = c.numPanels === 0 ||
        (Object.prototype.toString.call(c.panelWidths) === "[object Array]" && c.panelWidths.length === 0);
      return noPanels ? (parseInt(s.length, 10) || 0) > 0 : false;
    } catch (e) { return false; }
  }

  function sectionNums(fc) {
    try {
      return Object.keys(fc.state.sections || {}).map(Number)
        .filter(function (n) { return !isNaN(n); })
        .sort(function (a, b) { return a - b; });
    } catch (e) { return []; }
  }

  // 2026-09-07: true once the calculator itself has drawn this section on Review.
  // Any real wrapper carries a "Section N" header and is NOT one of ours.
  function alreadyDrawn(host, n) {
    try {
      var wraps = host.querySelectorAll(".section-preview-wrapper");
      var re = new RegExp("^\\s*Section\\s+" + n + "\\b");
      for (var i = 0; i < wraps.length; i++) {
        if (wraps[i].hasAttribute("data-cf-gate-only")) continue;
        if (re.test(wraps[i].textContent || "")) return true;
      }
      return false;
    } catch (e) { return false; }
  }

  function box(label, isGate) {
    var b = document.createElement("div");
    b.setAttribute("style", "text-align:center;font-size:11px;color:#475569;" + (isGate ? "flex:0 0 90px;" : "flex:0 0 14px;"));
    var g = document.createElement("div");
    g.setAttribute("style", "height:" + (isGate ? "70px" : "86px") + ";background:" + (isGate ? "#dbeafe" : "#1a1a1a") + ";border-radius:2px;margin-bottom:4px;");
    var l = document.createElement("div");
    l.textContent = label;
    b.appendChild(g); b.appendChild(l);
    return b;
  }

  function addGateOnlyBlocks(fc) {
    try {
      var host = document.querySelector("#final-sections-container .final-sections-container");
      if (!host) return;
      sectionNums(fc).forEach(function (n) {
        var s = fc.state.sections[n];
        if (!s || !s.length) return;
        if (!isGateOnly(fc, n)) return;
        if (host.querySelector('[data-cf-gate-only="' + n + '"]')) return;
        if (alreadyDrawn(host, n)) return;   // calculator drew it properly — stand down

        var wrap = document.createElement("div");
        wrap.className = "section-preview-wrapper";
        wrap.setAttribute("data-cf-gate-only", String(n));
        wrap.setAttribute("style", "background:#f8fafc;border:1px solid #e2e8f0;border-radius:8px;padding:12px;margin-bottom:10px;width:100%;box-sizing:border-box;");

        var hdr = document.createElement("div");
        hdr.className = "section-preview-header";
        hdr.setAttribute("style", "display:flex;justify-content:space-between;align-items:center;font-size:14px;font-weight:600;color:#334155;margin-bottom:8px;");
        var name = document.createElement("span");
        name.textContent = "Section " + n;
        var det = document.createElement("span");
        det.className = "section-preview-details";
        det.setAttribute("style", "font-size:11px;color:#64748b;font-weight:normal;line-height:1.3;text-align:left;flex:1 1 auto;margin-left:10px;");
        var gw = parseInt(s.gateLength, 10) || 0;
        det.textContent = "Length: " + s.length + "mm | Gate only | Gate: " + gw + "mm";
        hdr.appendChild(name); hdr.appendChild(det);
        wrap.appendChild(hdr);

        var strip = document.createElement("div");
        strip.className = "section-panels-container";
        strip.setAttribute("style", "display:flex;align-items:flex-end;justify-content:center;gap:2px;width:100%;");
        strip.appendChild(box("50", false));
        strip.appendChild(box(gw + " Gate", true));
        strip.appendChild(box("50", false));
        wrap.appendChild(strip);

        var existing = [].slice.call(host.children), placed = false;
        for (var i = 0; i < existing.length; i++) {
          var m = (existing[i].textContent || "").match(/Section\s+(\d+)/);
          if (m && Number(m[1]) > n) { host.insertBefore(wrap, existing[i]); placed = true; break; }
        }
        if (!placed) host.appendChild(wrap);
      });
    } catch (e) {}
  }

  var timer = null;
  function schedule() {
    if (timer) clearTimeout(timer);
    timer = setTimeout(function () { addGateOnlyBlocks(inst()); }, 200);
  }

  function install() {
    var fc = inst();
    if (!fc || !fc.state) return false;
    var P = Object.getPrototypeOf(fc);
    if (!P || P.__cfGateOnlySection) return true;

    if (typeof P.validateSection === "function") {
      var orig = P.validateSection;
      P.validateSection = function (n) {
        var r = orig.apply(this, arguments);
        if (r) return r;
        try { if (isGateOnly(this, n)) return true; } catch (e) {}
        return r;
      };
    }

    if (typeof P.updateFinalSectionsPreview === "function") {
      var o2 = P.updateFinalSectionsPreview;
      P.updateFinalSectionsPreview = function () {
        var r = o2.apply(this, arguments);
        try { addGateOnlyBlocks(this); } catch (e) {}
        return r;
      };
    }

    try {
      var root = document.querySelector(".calculator-content") || document.body;
      if (window.MutationObserver) new MutationObserver(schedule).observe(root, { childList: true, subtree: true });
    } catch (e) {}

    P.__cfGateOnlySection = true;
    schedule();
    try { console.log("[cf-gate-only-section] installed"); } catch (e) {}
    return true;
  }

  function tryInstall() {
    var done = false;
    try { done = install(); } catch (e) {}
    if (!done && ++TRIES < MAX_TRIES) setTimeout(tryInstall, 100);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", tryInstall);
  } else {
    tryInstall();
  }
})();
